import type { TError } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';
import type { TFormQuestion } from './get-form';

export interface TFormSubmissionAnswerWithQuestion {
  id: string;
  submissionId: string;
  questionId: string;
  selectedOptionIds: string[];
  orderingSequence: string[];
  textAnswer: string | null;
  fileUrls: string[];
  /** Structured answer — `{ grades: { [rowId]: number } }` for multiple_grading. */
  value: Record<string, unknown> | null;
  question: TFormQuestion;
}

/**
 * Submission detail with answers and question details
 */
export interface TSubmissionResult {
  id: string;
  formId: string;
  userId: string;
  submissionNumber: number;
  status: 'in_progress' | 'submitted';
  startedAt: string;
  submittedAt: string | null;
  /** Hands-On Practice: the teacher rejected it instead of reviewing it. */
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  answers: TFormSubmissionAnswerWithQuestion[];
  form?: {
    id: string;
    title: string;
  };
}

export const useGetFormSubmissionQueryKey = (id: string) => [ENTITY, 'form-submission', id];

/**
 * On-demand fetcher (no React hook) for a form submission. Useful for
 * imperatively pulling a submission when the user clicks an action like
 * "Download PDF" without keeping the query subscribed.
 */
export const fetchFormSubmission = async (id: string): Promise<TSubmissionResult> => {
  const res = await axios.get<TSubmissionResult>(`/community-hub/form-submissions/${id}`);
  return res.data;
};

/**
 * Hook to fetch a specific form submission by ID with answers and question details
 *
 * @param id - Submission ID (UUID)
 */
export const useGetFormSubmission = (id: string) => {
  return useQuery<TSubmissionResult, TError>({
    queryKey: useGetFormSubmissionQueryKey(id),
    queryFn: () => fetchFormSubmission(id),
    enabled: !!id,
  });
};
